import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import './CakeDetail.css';
import { useSettings } from '../context/SettingsContext';
import { api } from '../lib/api';


export default function CakeDetail({ addToCart }) {
    const { id } = useParams();
    const navigate = useNavigate();
    const SITE_CONFIG = useSettings();
    const [cake, setCake] = useState(null);
    const [related, setRelated] = useState([]);
    const [loading, setLoading] = useState(true);
    const [flavour, setFlavour] = useState(0);
    const [selectedPound, setSelectedPound] = useState(0);
    const [qty, setQty] = useState(1);
    const [added, setAdded] = useState(false);
    const [tab, setTab] = useState('description');
    const [mainIndex, setMainIndex] = useState(0);

    useEffect(() => {
        let mounted = true;
        setLoading(true);
        setFlavour(0);
        api.getCake(id)
            .then(({ cake }) => {
                if (!mounted) return;
                setCake(cake);
                if (cake.poundSizes && cake.poundSizes.length > 0) {
                    setSelectedPound(Math.min(...cake.poundSizes));
                }
                return api.getCakes({ category: cake.category }).then(({ cakes }) => {
                    if (!mounted) return;
                    setRelated(cakes.filter(c => c.id !== cake.id).slice(0, 3));
                });
            })
            .catch(() => { if (mounted) setCake(null); })
            .finally(() => { if (mounted) setLoading(false); });
        return () => { mounted = false; };
    }, [id]);

    // Reset main image index whenever a new cake is loaded
    useEffect(() => { setMainIndex(0); }, [cake]);

    if (loading) return (
        <div className="detail-page">

            {/* Breadcrumb skeleton */}
            <div className="breadcrumb">
                <span className="skel skel-text" style={{ width: 40, height: 11 }} />
                <span className="skel-sep">›</span>
                <span className="skel skel-text" style={{ width: 40, height: 11 }} />
                <span className="skel-sep">›</span>
                <span className="skel skel-text" style={{ width: 90, height: 11 }} />
            </div>

            <div className="detail-layout">

                {/* Left — Image skeleton */}
                <div className="detail-img-col">
                    <div className="skel skel-img" />
                    <div className="thumb-strip">
                        <div className="skel skel-thumb" />
                        <div className="skel skel-thumb" />
                        <div className="skel skel-thumb" />
                    </div>
                </div>

                {/* Right — Info skeleton */}
                <div className="detail-info-col">
                    <span className="skel skel-text" style={{ width: 110, height: 11, marginBottom: 12 }} />
                    <span className="skel skel-text" style={{ width: '78%', height: 42, marginBottom: 18 }} />
                    <div className="detail-rule" />
                    <span className="skel skel-text" style={{ width: '95%', height: 16, marginTop: 20 }} />
                    <span className="skel skel-text" style={{ width: '68%', height: 16, marginTop: 10, marginBottom: 26 }} />

                    <div className="detail-meta">
                        <span className="skel skel-pill" />
                        <span className="skel skel-pill" />
                        <span className="skel skel-pill" />
                    </div>

                    <span className="skel skel-text" style={{ width: 100, height: 11, marginBottom: 12 }} />
                    <div className="flavour-options" style={{ marginBottom: 28 }}>
                        <span className="skel skel-flavour" />
                        <span className="skel skel-flavour" />
                        <span className="skel skel-flavour" />
                    </div>

                    <div className="detail-purchase">
                        <span className="skel skel-text" style={{ width: 130, height: 34 }} />
                        <span className="skel skel-qty" />
                    </div>

                    <div className="detail-actions">
                        <span className="skel skel-btn" />
                        <span className="skel skel-btn" />
                    </div>
                </div>
            </div>

            {/* Tabs skeleton */}
            <div className="detail-tabs-section">
                <div className="detail-tabs">
                    <span className="skel skel-tab" />
                    <span className="skel skel-tab" />
                    <span className="skel skel-tab" />
                </div>
                <div className="detail-tab-body">
                    <span className="skel skel-text" style={{ width: '100%', height: 16, marginBottom: 12 }} />
                    <span className="skel skel-text" style={{ width: '92%', height: 16, marginBottom: 12 }} />
                    <span className="skel skel-text" style={{ width: '96%', height: 16, marginBottom: 12 }} />
                    <span className="skel skel-text" style={{ width: '60%', height: 16 }} />
                </div>
            </div>
        </div>
    );

    if (!cake) return (
        <div className="detail-notfound">
            <h2>Cake not found</h2>
            <Link to="/" className="btn-primary">Back to Home</Link>
        </div>
    );

    const flavours = cake.flavours && cake.flavours.length > 0 ? cake.flavours : ['Standard'];
    const displayPrice = (cake.pricePerPound && cake.pricePerPound > 0 && selectedPound > 0) ? (cake.pricePerPound * selectedPound) : cake.price;

    // Build images array from gallery if present, otherwise fallback to single image
    const images = (cake.gallery && cake.gallery.length > 0) ? cake.gallery.map(g => g.url) : [cake.image];

    const handleAdd = () => {
        for (let i = 0; i < qty; i++) {
            addToCart({ 
                ...cake, 
                selectedFlavour: flavours[flavour],
                selectedPound: selectedPound > 0 ? selectedPound : null,
                price: displayPrice
            });
        }
        setAdded(true);
        setTimeout(() => setAdded(false), 2200);
    };

    const handleWhatsApp = () => {
        const poundText = selectedPound > 0 ? `%0AWeight: ${selectedPound} lbs` : '';
        const msg = `Hello ${SITE_CONFIG.brandName}! 🎂%0A%0AI'd like to order:%0A*${cake.name}*${poundText}%0AFlavour: ${flavours[flavour]}%0AQuantity: ${qty}%0ATotal: PKR ${(displayPrice * qty).toLocaleString()}`;
        window.open(`https://wa.me/${SITE_CONFIG.whatsapp}?text=${msg}`, '_blank');
    };

    return (
        <div className="detail-page">

            {/* Breadcrumb */}
            <div className="breadcrumb">
                <Link to="/">Home</Link>
                <span>›</span>
                <Link to="/">Cakes</Link>
                <span>›</span>
                <span className="bc-active">{cake.name}</span>
            </div>

            {/* Main detail section */}
            <div className="detail-layout">

                {/* Left — Image */}
                <div className="detail-img-col">
                    <div className="detail-img-wrap">
                        <img src={images[mainIndex]} alt={cake.name} className="detail-img" />
                        {cake.badge && <span className="detail-badge">{cake.badge}</span>}
                        <div className="detail-logo-mark">
                            <span>P</span>
                        </div>
                    </div>
                    {/* Thumbnail strip — render only when multiple images exist */}
                    {images.length > 1 && (
                      <div className="thumb-strip">
                        {images.map((img, i) => (
                            <div key={i} className={`thumb ${i === mainIndex ? 'active' : ''}`} onClick={() => setMainIndex(i)}>
                                <img src={img} alt={`view-${i}`} />
                            </div>
                        ))}
                      </div>
                    )}
                </div>

                {/* Right — Info */}
                <div className="detail-info-col">

                    <p className="detail-category">{cake.category}</p>
                    <h1 className="detail-name">{cake.name}</h1>
                    <div className="detail-rule" />

                    <p className="detail-short-desc">{cake.description}</p>

                    {/* Meta pills */}
                    <div className="detail-meta">
                        <div className="meta-pill">
                            <span className="meta-label">Serves</span>
                            <span className="meta-val">{cake.serves}</span>
                        </div>
                        <div className="meta-pill">
                            <span className="meta-label">Weight</span>
                            <span className="meta-val">{selectedPound > 0 ? `${selectedPound} lbs` : (cake.weight || 'lbs')}</span>
                        </div>
                        <div className="meta-pill">
                            <span className="meta-label">Delivery</span>
                            <span className="meta-val">{cake.delivery}</span>
                        </div>
                        {cake.customizable && (
                            <div className="meta-pill custom">
                                <span className="meta-val">✦ Customizable</span>
                            </div>
                        )}
                    </div>

                    {/* Pound selector */}
                    {cake.poundSizes && cake.poundSizes.length > 0 && (
                        <div className="flavour-section">
                            <p className="flavour-label">Select Size</p>
                            <div className="flavour-options">
                                {cake.poundSizes.sort((a, b) => a - b).map((size, i) => (
                                    <button
                                        key={i}
                                        className={`flavour-btn ${selectedPound === size ? 'active' : ''}`}
                                        onClick={() => setSelectedPound(size)}
                                    >
                                        {size} lbs
                                    </button>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* Flavour selector */}
                    <div className="flavour-section">
                        <p className="flavour-label">Select Flavour</p>
                        <div className="flavour-options">
                            {flavours.map((f, i) => (
                                <button
                                    key={i}
                                    className={`flavour-btn ${flavour === i ? 'active' : ''}`}
                                    onClick={() => setFlavour(i)}
                                >
                                    {f}
                                </button>
                            ))}
                        </div>
                    </div>

                    {/* Qty + Price */}
                    <div className="detail-purchase">
                        <div className="detail-price">PKR {(displayPrice * qty).toLocaleString()}</div>
                        <div className="qty-control">
                            <button onClick={() => setQty(q => Math.max(1, q - 1))}>−</button>
                            <span>{qty}</span>
                            <button onClick={() => setQty(q => q + 1)}>+</button>
                        </div>
                    </div>

                    {/* Action buttons */}
                    <div className="detail-actions">
                        <button
                            className={`btn-add-cart ${added ? 'added' : ''}`}
                            onClick={handleAdd}
                        >
                            {added ? '✓ Added to Order' : 'Add to Order'}
                        </button>
                        <button className="btn-whatsapp-detail" onClick={handleWhatsApp}>
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                                <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
                            </svg>
                            Order on WhatsApp
                        </button>
                    </div>

                    <Link to="/order" className="view-order-link">View full order →</Link>

                </div>
            </div>

            {/* Tabs — Description / Details */}
            <div className="detail-tabs-section">
                <div className="detail-tabs">
                    <button className={`dtab ${tab === 'description' ? 'active' : ''}`} onClick={() => setTab('description')}>Description</button>
                    <button className={`dtab ${tab === 'details' ? 'active' : ''}`} onClick={() => setTab('details')}>Details</button>
                    <button className={`dtab ${tab === 'delivery' ? 'active' : ''}`} onClick={() => setTab('delivery')}>Delivery & Custom</button>
                </div>
                <div className="detail-tab-body">
                    {tab === 'description' && (
                        <div className="tab-content">
                            {(cake.longDescription || cake.description || '').split('\n\n').map((para, i) => (
                                <p key={i}>{para}</p>
                            ))}
                        </div>
                    )}
                    {tab === 'details' && (
                        <div className="tab-content tab-table">
                            {[
                                ['Category', cake.category],
                                ['Serves', cake.serves],
                                ['Weight', selectedPound > 0 ? `${selectedPound} lbs` : (cake.weight || 'lbs')],
                                ['Available Flavours', flavours.join(', ')],
                                ['Customizable', cake.customizable ? 'Yes' : 'No'],
                            ].map(([k, v]) => (
                                <div className="tab-row" key={k}>
                                    <span className="tab-key">{k}</span>
                                    <span className="tab-val">{v}</span>
                                </div>
                            ))}
                        </div>
                    )}
                    {tab === 'delivery' && (
                        <div className="tab-content">
                            <p>We deliver across <strong>Karachi, Pakistan</strong>. Orders require advance notice as all cakes are freshly baked to order.</p>
                            <p>Advance notice required: <strong>{cake.delivery}</strong>.</p>
                            <p>Delivery charges are tentative and vary by area — we'll confirm the exact amount with you on WhatsApp before finalizing your order.</p>
                            <p>For urgent orders, please contact us directly on <strong>{SITE_CONFIG.whatsappDisplay}</strong>.</p>
                        </div>
                    )}
                </div>
            </div>

            {/* Related cakes */}
            {related.length > 0 && (
                <div className="related-section">
                    <p className="section-subtitle">You Might Also Like</p>
                    <h2 className="section-title" style={{ fontSize: 36 }}>More from {cake.category}</h2>
                    <div className="detail-rule" style={{ maxWidth: 300, margin: '16px auto 32px' }} />
                    <div className="related-grid">
                        {related.map(rc => (
                            <div
                                key={rc.id}
                                className="related-card"
                                onClick={() => { navigate(`/cake/${rc.id}`); window.scrollTo(0, 0); }}
                            >
                                <div className="related-img-wrap">
                                    <img src={rc.image} alt={rc.name} />
                                    <div className="cake-logo-mark">
                                        <span>P</span>
                                    </div>
                                </div>
                                <div className="related-info">
                                    <p className="cake-category">{rc.category}</p>
                                    <h3 className="related-name">{rc.name}</h3>
                                    <p className="related-price">PKR {rc.price.toLocaleString()}</p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            )}

        </div>
    );
}